import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import Eyebrow from "./Eyebrow.jsx";
import "./Team.css";

const TEAM = [
  { name: "Md Mehedi Al Mahmud", role: "Chief Executive Officer", photo: "/assets/images/team/mehedi.webp", linkedin: "https://www.linkedin.com/in/almahmud801/" },
  { name: "Hasin Mahtab Alvee", role: "Tech Lead", photo: "/assets/images/team/alvee.webp", linkedin: "https://www.linkedin.com/in/hasin-mahtab-4b9640217/" },
  { name: "Mehnaz Tamanna Oyeshi", role: "Marketing Lead", photo: "/assets/images/team/mehnaz.webp", linkedin: "https://www.linkedin.com/in/mehnaz-oyeshi/" },
  { name: "Hasibul Islam Nirjhar", role: "Engineering Strategy Lead", photo: "/assets/images/team/nirjhar.webp", linkedin: "https://www.linkedin.com/in/hasibul-islam-nirjhar-b50925262/" },
  { name: "Ahabab Imtiaz Risat", role: "Software Development Lead", photo: "/assets/images/team/risat.webp", linkedin: "https://www.linkedin.com/in/imtiazrisat/" },
  { name: "Mainul Hasan Siam", role: "AI Engineer Lead", photo: "/assets/images/team/mainul.webp", linkedin: "https://www.linkedin.com/in/md-mainul-hasan-iut173/" },
];

const LINKEDIN =
  "M5.40161 2.5C4.48974 2.5 3.75 3.23852 3.75 4.15039C3.75 5.06227 4.48914 5.81787 5.40039 5.81787C6.31164 5.81787 7.052 5.06227 7.052 4.15039C7.052 3.23914 6.31349 2.5 5.40161 2.5ZM13.4595 6.875C12.0726 6.875 11.2791 7.60028 10.8984 8.32153H10.8582V7.06909H8.125V16.25H10.9729V11.7053C10.9729 10.5078 11.0633 9.35059 12.5452 9.35059C14.0058 9.35059 14.0271 10.716 14.0271 11.781V16.25H16.8713H16.875V11.2073C16.875 8.73978 16.3445 6.875 13.4595 6.875ZM3.97705 7.06909V16.25H6.82739V7.06909H3.97705Z";

function Arrow({ flip }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" style={flip ? { transform: "scaleX(-1)" } : undefined}>
      <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const AUTOPLAY_MS = 3200; // how long each person holds the screen
const RESUME_MS = 6000; // how long auto-advance stays quiet after the visitor touches the row

/* The row's own motion. All of it is one tween on one scroller, so it can be interrupted at any moment:
   - a step to the next card (a button press responds with ease-out; auto-advance glides with ease-in-out)
   - after the last person, one glide back to the first (ease-in-out, no bounce: it lands, it doesn't ricochet) */
const STEP = { duration: 0.85, ease: "power3.inOut" };
const PRESS = { duration: 0.6, ease: "power3.out" };
// quicker than a step per card travelled, and a softer curve than the steps so the long sweep never feels yanked
const rewind = (cards) => ({ duration: Math.min(1.25, 0.7 + 0.1 * cards), ease: "power2.inOut" });

export default function Team() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const tweenRef = useRef(null);
  const holdRef = useRef(false); // pointer or keyboard focus is inside the section
  const resumeAtRef = useRef(0);
  const [first, setFirst] = useState(0);
  const [atEnd, setAtEnd] = useState(false);

  // one card to the next: card width plus the gap
  const stride = () => {
    const [a, b] = trackRef.current?.children ?? [];
    return a && b ? b.offsetLeft - a.offsetLeft : 1;
  };
  const maxScroll = () => trackRef.current.scrollWidth - trackRef.current.clientWidth;

  function stop() {
    const el = trackRef.current;
    tweenRef.current?.kill();
    tweenRef.current = null;
    if (el) {
      // hand the scroller back to the browser: native snapping settles wherever the visitor left it
      el.style.scrollSnapType = "";
      el.style.scrollBehavior = "";
    }
  }

  function glideTo(left, { duration, ease }) {
    const el = trackRef.current;
    stop();
    const to = Math.max(0, Math.min(maxScroll(), left));
    if (!duration || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.scrollLeft = to;
      return;
    }
    // snapping would fight a scripted scroll, so it waits until the glide has landed
    el.style.scrollSnapType = "none";
    el.style.scrollBehavior = "auto";
    const pos = { x: el.scrollLeft };
    tweenRef.current = gsap.to(pos, {
      x: to,
      duration,
      ease,
      onUpdate: () => (el.scrollLeft = pos.x),
      onComplete: stop,
    });
  }

  function onScroll() {
    const el = trackRef.current;
    setFirst(Math.round(el.scrollLeft / stride()));
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }

  function step(dir, motion = PRESS) {
    const el = trackRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / stride());
    glideTo((index + dir) * stride(), motion);
  }

  // Auto-advance. It waits while the row is off screen or the tab is hidden, stays still while the pointer or
  // keyboard focus is in the section and for a few seconds after the visitor touched the row, and is off for
  // reduced motion. At the end it glides all the way back to the first person.
  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let visible = false;
    const io = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), { threshold: 0.35 });
    io.observe(section);
    const setHold = (on) => () => (holdRef.current = on);
    const enter = setHold(true);
    const leave = setHold(false);
    // any touch of the row takes over at once: the glide in flight is dropped where it is, never finished for them
    const touched = () => {
      stop();
      resumeAtRef.current = Date.now() + RESUME_MS;
    };
    const events = ["pointerdown", "wheel", "touchstart", "keydown"];
    section.addEventListener("pointerenter", enter);
    section.addEventListener("pointerleave", leave);
    section.addEventListener("focusin", enter);
    section.addEventListener("focusout", leave);
    events.forEach((type) => track.addEventListener(type, touched, { passive: true }));

    const id = setInterval(() => {
      if (!visible || document.hidden || holdRef.current || Date.now() < resumeAtRef.current || tweenRef.current) return;
      if (track.scrollLeft >= maxScroll() - 4) {
        const cards = Math.round(track.scrollLeft / stride());
        glideTo(0, rewind(cards));
      } else step(1, STEP);
    }, AUTOPLAY_MS);

    return () => {
      clearInterval(id);
      io.disconnect();
      stop();
      section.removeEventListener("pointerenter", enter);
      section.removeEventListener("pointerleave", leave);
      section.removeEventListener("focusin", enter);
      section.removeEventListener("focusout", leave);
      events.forEach((type) => track.removeEventListener(type, touched));
    };
    // glideTo, step and stop only touch refs and the DOM
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = Math.min(1, (first + 1) / TEAM.length);

  return (
    <section ref={sectionRef} className="section team tm" id="team-section" aria-labelledby="team-heading">
      <div className="padding-global">
        <div className="container-base">
          <header className="tm-head">
            <div className="tm-head-text">
              <Eyebrow section="team" />
              <h2 id="team-heading" className="heading-style-h2 tm-title">
                Meet the minds behind Version3
              </h2>
            </div>
            <div className="tm-nav">
              <span className="tm-count" aria-hidden="true">
                {String(first + 1).padStart(2, "0")} <i>/ {String(TEAM.length).padStart(2, "0")}</i>
              </span>
              <button type="button" className="tm-btn" aria-label="Previous team member" disabled={first === 0} onClick={() => step(-1)}>
                <Arrow />
              </button>
              <button type="button" className="tm-btn" aria-label="Next team member" disabled={atEnd} onClick={() => step(1)}>
                <Arrow flip />
              </button>
            </div>
          </header>
        </div>
      </div>

      <ul ref={trackRef} className="tm-track" onScroll={onScroll} aria-label="Team members">
        {TEAM.map((m) => (
          <li key={m.name} className="tm-card">
            <div className="tm-photo">
              <img src={m.photo} alt={m.name} width="800" height="1000" loading="lazy" />
              <div className="tm-socials">
                <a className="tm-social" href={m.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${m.name} on LinkedIn`}>
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d={LINKEDIN} fill="currentColor" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="tm-meta">
              <b>{m.name}</b>
              <span>{m.role}</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="padding-global">
        <div className="container-base">
          <div className="tm-progress" aria-hidden="true">
            <i style={{ transform: `scaleX(${progress})` }} />
          </div>
        </div>
      </div>
    </section>
  );
}
